import { Config } from "@config";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import type { AbstractStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import {
	faBuildingColumns as farBuildingColumns,
	faMoneyBill1 as farMoneyBill1,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faCalendar as fasCalendar,
	faArrowRight as fasArrowRight,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import { http, Button, FormCurrency, FormNumber, FormSelect } from "bm-ui-kit";
import type { FormSelectOptionsFn } from "bm-ui-kit";
import dayjs from "dayjs";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { EMPLOYMENT_TYPES } from "../types";
import type { PersonalLoanScoringCalculatorFormFields } from "../types";

interface StepFields {
	lastIncome1: number;
	lastIncome2: number;
	lastIncome3: number;
	receiptBank: string;
	employmentType: string;
	birthYear: number;
}

interface Props extends AbstractStepProps {
	values: PersonalLoanScoringCalculatorFormFields;
}

export class Step3 extends AbstractStep<StepFields, Props> {
	protected initValues(): StepFields {
		const {
			values: { incomeList = [] },
		} = this.props;
		const defaultIncomeSum = incomeList.reduce((acc, ic) => acc + ic.amount, 0);

		return {
			lastIncome1: defaultIncomeSum,
			lastIncome2: defaultIncomeSum,
			lastIncome3: defaultIncomeSum,
			receiptBank: undefined,
			employmentType: undefined,
			birthYear: undefined,
		};
	}

	@autobind
	private loadBanks(): FormSelectOptionsFn<string> {
		const apiAddress = Config.getApiUrl("/api/public/bank/RETAIL_ACCOUNT");

		return {
			id: apiAddress,
			fn: () =>
				http.getWithCache(apiAddress).then((response) =>
					response.map((item) => ({
						label: item.name,
						value: item.code,
					})),
				),
		};
	}

	protected renderContent(): ReactNode {
		return (
			<>
				<Card.Body>
					<FormCurrency name="lastIncome1" label="Utolsó három havi jövedelem" required />
					<FormCurrency name="lastIncome2" required />
					<FormCurrency name="lastIncome3" required />
					<FormSelect
						label="Melyik az elsődleges számlavezető bankod?"
						name="receiptBank"
						prepend={<FontAwesomeIcon icon={farBuildingColumns} />}
						placeholder="Kérjük, válassz!"
						options={this.loadBanks()}
						required
					/>
					<FormSelect
						label="Munkaszerződés típusa"
						name="employmentType"
						prepend={<FontAwesomeIcon icon={farMoneyBill1} />}
						placeholder="Válassz ki egy munkaviszony típust!"
						options={EMPLOYMENT_TYPES}
						required
					/>
					<FormNumber
						label="Melyik évben születtél?"
						placeholder="Add meg a születési éved!"
						name="birthYear"
						maxLength={4}
						prepend={<FontAwesomeIcon icon={fasCalendar} />}
						validate={this.validBirthYear}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<Button type="submit" icon={<FontAwesomeIcon icon={fasArrowRight} />}>
						Tovább
					</Button>
				</Card.Footer>
			</>
		);
	}

	private validBirthYear(year: number): string {
		if (!year) {
			return "Kérjük adjon meg születési évet!";
		}

		if (year < 1900) {
			return "A születési éve nem lehet kisebb mint 1900";
		}

		const actualYear = dayjs().year();

		if (year > actualYear) {
			return `A születési éve nem lehet nagyobb mint ${actualYear}`;
		}
	}

	protected getComponentName(): string {
		return "Step3";
	}
}
